package interviewprep.client

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import interviewprep.common.InputMode
import interviewprep.common.PracticeMode
import interviewprep.common.ProductBrand
import interviewprep.common.SessionSnapshotV1
import interviewprep.common.TurnEnvelopeV3
import interviewprep.common.TurnTransitionHint
import interviewprep.common.VoiceQuestion
import kotlinx.browser.document
import kotlinx.browser.localStorage
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.attributes.value
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Header
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.renderComposable

fun main() {
    document.title = ProductBrand.APP_NAME
    renderComposable(rootElementId = "root") {
        App()
    }
}

@Composable
private fun App() {
    val scope = rememberCoroutineScope()
    var userId by remember { mutableStateOf(loadOrCreateUserId()) }
    var selectedTrack by remember { mutableStateOf(loadSelectedTrack()) }
    var selectedPracticeMode by remember { mutableStateOf(loadPracticeMode()) }
    var sessionId by remember { mutableStateOf(loadSessionId()) }
    var sessionSnapshot by remember { mutableStateOf(loadSessionSnapshot()) }
    var activeQuestion by remember {
        mutableStateOf(sessionSnapshot?.currentQuestion?.let(::toVoiceQuestion))
    }
    var timeline by remember { mutableStateOf(loadTimeline()) }
    var answerText by remember { mutableStateOf("") }
    var voiceDraft by remember { mutableStateOf("") }
    var liveSpeechText by remember { mutableStateOf("") }
    var status by remember { mutableStateOf("Ready") }
    var captureStatus by remember { mutableStateOf("Ready") }
    var isBusy by remember { mutableStateOf(false) }
    var isSpeaking by remember { mutableStateOf(false) }
    var isListening by remember { mutableStateOf(false) }
    var recognitionHandle by remember { mutableStateOf<dynamic>(null) }
    var speechEnabled by remember { mutableStateOf(browserSpeechRecognitionAvailable()) }

    fun persistSession(newSessionId: String, snapshot: SessionSnapshotV1) {
        sessionId = newSessionId
        sessionSnapshot = snapshot
        persistSessionSnapshot(newSessionId, snapshot)
    }

    fun setTimeline(next: List<TimelineEntry>) {
        timeline = next
        persistTimeline(next)
    }

    fun appendTimeline(entry: TimelineEntry) {
        setTimeline((timeline + entry).takeLast(24))
    }

    fun clearSession() {
        stopSpeechRecognition(recognitionHandle)
        recognitionHandle = null
        isSpeaking = false
        isListening = false
        sessionId = null
        sessionSnapshot = null
        activeQuestion = null
        answerText = ""
        voiceDraft = ""
        liveSpeechText = ""
        captureStatus = "Ready"
        localStorage.removeItem(SESSION_ID_KEY)
        localStorage.removeItem(SESSION_SNAPSHOT_KEY)
        localStorage.removeItem(TIMELINE_KEY)
        setTimeline(emptyList())
        stopSpeaking()
        status = "Ready"
    }

    var beginSpeechCapture: () -> Unit = {}

    fun speakQuestion(question: VoiceQuestion?, listenAfterPlayback: Boolean = false) {
        val spoken = questionSpeechText(question)
        if (!spoken.isNullOrBlank()) {
            speak(
                text = spoken,
                onStart = {
                    isSpeaking = true
                    captureStatus = "Interviewer speaking..."
                    status = captureStatus
                },
                onDone = {
                    isSpeaking = false
                    captureStatus = "Ready for your answer"
                    status = captureStatus
                    if (listenAfterPlayback && sessionSnapshot != null && speechEnabled && !isListening && !isBusy) {
                        beginSpeechCapture()
                    }
                },
                onError = { message ->
                    isSpeaking = false
                    captureStatus = message
                    status = message
                    if (listenAfterPlayback && sessionSnapshot != null && speechEnabled && !isListening && !isBusy) {
                        beginSpeechCapture()
                    }
                }
            )
        }
    }

    fun handleEnvelope(envelope: TurnEnvelopeV3) {
        persistSession(envelope.sessionId, envelope.session)
        activeQuestion = envelope.question ?: envelope.session.currentQuestion?.let(::toVoiceQuestion)
        val feedback = envelope.feedback
        if (feedback != null) {
            appendTimeline(feedback.toTimelineEntry(envelope.transitionHint))
            val mentorSpeech = feedback.mentorSpeech?.trim()
            if (!mentorSpeech.isNullOrBlank()) {
                speak(
                    text = cleanInterviewText(mentorSpeech),
                    onStart = {
                        isSpeaking = true
                        captureStatus = "Interviewer speaking..."
                        status = captureStatus
                    },
                    onDone = {
                        isSpeaking = false
                        captureStatus = "Ready for your answer"
                        status = captureStatus
                    },
                    onError = { message ->
                        isSpeaking = false
                        captureStatus = message
                        status = message
                    }
                )
            }
        }
        val question = envelope.question
        if (question != null) {
            appendTimeline(
                TimelineEntry(
                    role = TimelineRole.INTERVIEWER,
                    text = questionDisplayText(question),
                    detail = question.title
                )
            )
            if (feedback == null) {
                speakQuestion(question, listenAfterPlayback = true)
            }
        }
        status = when {
            envelope.transitionHint == TurnTransitionHint.INTERVIEW_COMPLETE -> "Interview complete"
            feedback != null && question != null -> "Feedback received. Continue with the next prompt."
            feedback != null -> "Feedback received"
            question != null -> "Question ready"
            else -> "Turn complete"
        }
    }

    fun startSession() {
        scope.launch {
            isBusy = true
            status = "Checking microphone permission..."
            captureStatus = status
            stopSpeaking()
            localStorage.setItem(TRACK_KEY, selectedTrack)
            localStorage.setItem(PRACTICE_MODE_KEY, selectedPracticeMode.name)
            try {
                if (!browserSpeechRecognitionAvailable()) {
                    speechEnabled = false
                    status = MicrophonePermissionResult.UNSUPPORTED.userMessage
                    captureStatus = status
                    return@launch
                }
                when (val permission = requestMicrophonePermission()) {
                    MicrophonePermissionResult.GRANTED -> {
                        speechEnabled = true
                        status = "Starting interview..."
                        captureStatus = permission.userMessage
                    }
                    MicrophonePermissionResult.UNSUPPORTED,
                    MicrophonePermissionResult.DENIED -> {
                        speechEnabled = false
                        status = permission.userMessage
                        captureStatus = permission.userMessage
                        return@launch
                    }
                }
                val response = startWebSession(
                    userId = userId,
                    track = selectedTrack,
                    practiceMode = selectedPracticeMode
                )
                persistSession(response.sessionId, response.session)
                activeQuestion = response.session.currentQuestion?.let(::toVoiceQuestion)
                setTimeline(emptyList())
                answerText = ""
                voiceDraft = ""
                liveSpeechText = ""
                status = "Preparing interviewer prompt..."
                captureStatus = status
                val envelope = sendStartTurn(
                    sessionId = response.sessionId,
                    snapshot = response.session,
                    inputMode = InputMode.VOICE
                )
                handleEnvelope(envelope)
            } catch (throwable: Throwable) {
                status = throwable.message ?: "Could not start interview"
                appendTimeline(TimelineEntry(TimelineRole.SYSTEM, status))
            } finally {
                isBusy = false
            }
        }
    }

    fun submitCapturedAnswer(answer: String, inputMode: InputMode) {
        val snapshot = sessionSnapshot
        val activeSessionId = sessionId
        val normalizedAnswer = answer.trim()
        if (snapshot == null || activeSessionId.isNullOrBlank()) {
            status = "Start an interview first"
            return
        }
        if (normalizedAnswer.isBlank()) {
            status = "Answer cannot be empty"
            return
        }
        scope.launch {
            isBusy = true
            status = "Evaluating answer..."
            stopSpeaking()
            appendTimeline(TimelineEntry(TimelineRole.CANDIDATE, normalizedAnswer))
            try {
                val envelope =
                    sendTurn(
                        sessionId = activeSessionId,
                        snapshot = snapshot,
                        answerText = normalizedAnswer,
                        inputMode = inputMode
                    )
                answerText = ""
                voiceDraft = ""
                handleEnvelope(envelope)
            } catch (throwable: Throwable) {
                status = throwable.message ?: "Could not evaluate answer"
                appendTimeline(TimelineEntry(TimelineRole.SYSTEM, status))
            } finally {
                isBusy = false
            }
        }
    }

    fun handleSpeechRecognitionEvent(event: SpeechRecognitionEvent) {
        when (event) {
            SpeechRecognitionEvent.Started -> {
                isListening = true
                captureStatus = "Recording. Speak your answer."
            }
            SpeechRecognitionEvent.AudioStarted -> {
                captureStatus = "Microphone is active."
            }
            SpeechRecognitionEvent.SoundStarted -> {
                captureStatus = "Sound detected. Keep speaking."
            }
            is SpeechRecognitionEvent.Text -> {
                answerText = event.value
                voiceDraft = event.value
                liveSpeechText = event.value
                captureStatus = if (event.isFinal) "Finishing your answer..." else "Listening..."
            }
            SpeechRecognitionEvent.SpeechEnded -> {
                captureStatus = "Processing speech..."
            }
            SpeechRecognitionEvent.NoMatch -> {
                captureStatus = "Speech was heard, but no transcript was recognized."
            }
            SpeechRecognitionEvent.Ended -> {
                isListening = false
                recognitionHandle = null
                val transcript = voiceDraft.trim()
                if (transcript.isNotBlank()) {
                    submitCapturedAnswer(transcript, InputMode.VOICE)
                } else if (sessionSnapshot != null) {
                    captureStatus =
                        if (captureStatus == "Sound detected. Keep speaking." || captureStatus == "Processing speech...") {
                            "Chrome heard audio but did not return a transcript. Try a longer sentence."
                        } else {
                            "No speech was captured. Try again."
                        }
                }
            }
            is SpeechRecognitionEvent.Failed -> {
                isListening = false
                recognitionHandle = null
                captureStatus = event.message
            }
        }
        status = captureStatus
    }

    beginSpeechCapture = {
        liveSpeechText = ""
        voiceDraft = ""
        val handle = startSpeechRecognition(::handleSpeechRecognitionEvent)
        if (handle == null) {
            speechEnabled = false
            captureStatus = "Speech recognition needs Chrome or Safari with microphone permission."
            status = captureStatus
        } else {
            recognitionHandle = handle
        }
    }

    Div({ classes("app-shell") }) {
        Header({ classes("topbar") }) {
            Div {
                Span({ classes("brand-mark") }) { Text("VI") }
                Span({ classes("brand-name") }) { Text(ProductBrand.APP_NAME) }
            }
            Div({ classes("topbar-actions") }) {
                Button(attrs = {
                    classes("ghost")
                    onClick { stopSpeaking() }
                }) { Text("Stop audio") }
                Button(attrs = {
                    classes("ghost")
                    onClick { clearSession() }
                }) { Text("Reset") }
            }
        }

        Div({ classes("workspace") }) {
            Div({ classes("setup-panel") }) {
                H1 { Text("Live mock interview") }
                P({ classes("lede") }) {
                    Text("Hear the interviewer, answer by voice, and get coaching after each turn.")
                }

                Div({ classes("field-grid") }) {
                    Label {
                        Span { Text("Round") }
                        Select(attrs = {
                            onInput {
                                selectedTrack = it.value ?: selectedTrack
                                localStorage.setItem(TRACK_KEY, selectedTrack)
                            }
                            if (isBusy) disabled()
                        }) {
                            trackOptions.forEach { option ->
                                Option(value = option.value, attrs = {
                                    if (option.value == selectedTrack) selected()
                                }) { Text(option.label) }
                            }
                        }
                    }
                    Label {
                        Span { Text("Mode") }
                        Select(attrs = {
                            onInput {
                                val nextValue = it.value ?: selectedPracticeMode.name
                                selectedPracticeMode = PracticeMode.valueOf(nextValue)
                                localStorage.setItem(PRACTICE_MODE_KEY, selectedPracticeMode.name)
                            }
                            if (isBusy) disabled()
                        }) {
                            Option(value = PracticeMode.STANDARD.name, attrs = {
                                if (selectedPracticeMode == PracticeMode.STANDARD) selected()
                            }) { Text("Interview") }
                            Option(value = PracticeMode.MENTOR.name, attrs = {
                                if (selectedPracticeMode == PracticeMode.MENTOR) selected()
                            }) { Text("Mentor") }
                        }
                    }
                }

                P({ classes("round-description") }) {
                    Text(trackOptions.firstOrNull { it.value == selectedTrack }?.description.orEmpty())
                }

                Div({ classes("button-row") }) {
                    Button(attrs = {
                        classes("primary")
                        if (isBusy) disabled()
                        onClick { startSession() }
                    }) { Text(if (sessionSnapshot == null) "Start interview" else "Start new interview") }
                    if (sessionSnapshot != null) {
                        Button(attrs = {
                            classes("secondary")
                            if (isBusy) disabled()
                            onClick { speakQuestion(activeQuestion, listenAfterPlayback = true) }
                        }) { Text("Replay prompt") }
                    }
                }

                Div({ classes("status-line") }) {
                    Span({
                        if (isBusy) {
                            classes("status-dot", "busy")
                        } else {
                            classes("status-dot")
                        }
                    }) { Text("") }
                    Text(status)
                }

                Div({ classes("capability-list") }) {
                    Capability("Browser speech recognition", if (speechEnabled) "Available" else "Unavailable")
                    Capability("Text to speech", if (browserTtsAvailable()) "Available" else "Unavailable")
                    Capability("Session", sessionId?.takeLast(10)?.let { "...$it" } ?: "Not started")
                }
            }

            Div({ classes("interview-panel") }) {
                Div({ classes("prompt-card") }) {
                    Span({ classes("eyebrow") }) {
                        Text(sessionSnapshot?.currentRoundIndex?.plus(1)?.let { "Round $it" } ?: "Prompt")
                    }
                    H2 {
                        Text(activeQuestion?.title?.takeIf { it.isNotBlank() } ?: "Start an interview")
                    }
                    P {
                        Text(questionDisplayText(activeQuestion))
                    }
                }

                Div({ classes("answer-panel") }) {
                    Div({ classes("answer-header") }) {
                        H3 { Text("Speak your answer") }
                        if (isListening) {
                            Span({ classes("recording-pill") }) { Text("Listening") }
                        }
                    }
                    VoiceLoopStage(
                        isSpeaking = isSpeaking,
                        isListening = isListening,
                        isBusy = isBusy,
                        sessionStarted = sessionSnapshot != null,
                        speechEnabled = speechEnabled,
                        liveSpeechText = liveSpeechText,
                        captureStatus = captureStatus
                    )
                    Div({ classes("voice-stage") }) {
                        Button(attrs = {
                            classes("voice-button")
                            if (isListening) classes("danger")
                            if (isBusy || (!speechEnabled && sessionSnapshot != null)) disabled()
                            onClick {
                                if (isListening) {
                                    stopSpeechRecognition(recognitionHandle)
                                    recognitionHandle = null
                                    isListening = false
                                    status = "Finishing your answer..."
                                } else if (sessionSnapshot == null) {
                                    startSession()
                                } else {
                                    beginSpeechCapture()
                                }
                            }
                        }) {
                            Text(
                                when {
                                    isListening -> "Stop recording"
                                    sessionSnapshot == null -> "Start interview"
                                    else -> "Start speaking"
                                }
                            )
                        }
                        P {
                            Text(
                                when {
                                    isListening -> "Listening now. Pause when you are done; the interview will continue automatically."
                                    !speechEnabled -> "Speech recognition needs Chrome or Safari with microphone permission."
                                    sessionSnapshot == null -> "This will ask for microphone permission before the first prompt."
                                    else -> "Voice is the primary mode. Your answer submits automatically when recording ends."
                                }
                            )
                        }
                    }
                }
            }
        }

        Div({ classes("timeline") }) {
            H3 { Text("Session timeline") }
            if (timeline.isEmpty()) {
                P({ classes("empty-state") }) { Text("Your interviewer prompts, answers, and coaching will appear here.") }
            } else {
                timeline.forEach { entry ->
                    Div({ classes("timeline-entry", entry.role.name.lowercase()) }) {
                        Span({ classes("entry-role") }) { Text(entry.role.label()) }
                        if (!entry.detail.isNullOrBlank()) {
                            Span({ classes("entry-detail") }) { Text(entry.detail) }
                        }
                        P { Text(entryDisplayText(entry)) }
                        if (entry.score != null) {
                            Span({ classes("score-pill") }) { Text("Score ${entry.score}/5") }
                        }
                    }
                }
            }
        }
    }
}
